import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser as useClerkUser } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Loader2 } from 'lucide-react';
import { ApiError } from '../lib/api';
import { completeOnboarding, getMe } from '../lib/api/users';
import { getPersona, LAUNCH_TOWN_SLUGS, type Persona } from '../lib/personas';
import {
  clearPendingRef,
  readPendingRef,
  readPersonalPage,
  writePersonalPage,
} from '../lib/storage';
type LoadState = 'reconciling' | 'needs-town' | 'error';
/**
 * Narrow an unknown candidate to a launch-town slug, or null.
 *
 * Only LAUNCH_TOWN_SLUGS are accepted: a known-but-not-yet-live town
 * (Newquay, Fowey — personas exist, catalogue not seeded), a stale
 * localStorage value, or a tampered Clerk metadata value all resolve to
 * null, routing the user to the picker rather than provisioning them
 * into a town the API cannot serve.
 */
function asLaunchTownSlug(candidate: unknown): string | null {
  return typeof candidate === 'string' && LAUNCH_TOWN_SLUGS.includes(candidate)
    ? candidate
    : null;
}
/**
 * Post-Clerk-auth landing page. Reconciles the Clerk session into a
 * TownInsider user row, hydrates the localStorage personalPage
 * route-hint, then forwards to /me.
 *
 * Three-layer town resolution
 * ---------------------------
 * The town slug needed to provision (or identify) a user row is resolved
 * from three sources in increasing order of cost, decreasing order of
 * locality:
 *   1. personalPage.town in localStorage — written by JoinPage when a
 *      /join/:town QR link is redeemed. Wiped on sign-out, on browser
 *      data clear, and on iOS storage pressure.
 *   2. Clerk publicMetadata.townSlug — stashed server-side by
 *      /invitation/start-signup (the partner-invitation flow, NOT the
 *      public /join?ref= flow). Survives the `ti:*` localStorage wipe
 *      but is only set for invitation-token sign-ups.
 *   3. GET /v1/users/me — the server is the authoritative source. If a
 *      row exists for this Clerk session, its town_slug + joinedAt are
 *      used to hydrate personalPage and route straight to /me, skipping
 *      reconcile entirely (the row already exists, attribution is
 *      first-touch and immutable, so there's nothing for /complete to
 *      do). If /me returns 404 (no row yet) we fall through to the
 *      picker — the genuine bare-URL first-touch case.
 *
 * The /me fallback (added 6 Jun 2026) makes the page self-healing
 * across the full population of returning-user paths: sign-out + sign-
 * in, browser data clear, private browsing transition, iOS storage
 * eviction, direct /sign-in arrival from any future auth surface we
 * haven't built yet. Without it, every public-QR guest who lost
 * localStorage saw the "Which town?" picker on re-sign-in even though
 * the server had their full state — unnecessary friction that broke
 * partner brand continuity.
 *
 * NOT a regression to the earlier buggy implementation
 * ----------------------------------------------------
 * An earlier version of this page used GET /me as the ONLY returning-
 * user branch: if /me failed, the user fell straight through to a
 * generic error. That broke the bare-URL first-touch path (no row
 * exists yet → 404 → error → user stuck). The current design is
 * different: /me is layer 3 of resolution, NOT a primary branch, and
 * a 404 from /me is treated as "no row yet, show the picker", not as
 * an error. Network failures and other non-404 errors still surface
 * the recoverable error UI.
 *
 * Single reconciliation path for first-touch
 * ------------------------------------------
 * Once a town slug is resolved from layers 1 or 2 (the local
 * non-interactive sources), reconciliation is one call: POST
 * /v1/users/invitation/complete. That endpoint is idempotent — it
 * returns the existing row if there is one (200) and creates it
 * otherwise (201). For layer 3 (server-resolved), we skip reconcile
 * entirely: the row already exists by definition.
 *
 * Partner attribution
 * -------------------
 * If the user arrived via a `/join/:town?ref=<partner>` URL, JoinPage
 * wrote the partner slug to `pendingRef` in localStorage. It is consumed
 * by reconcile on the create path (first-touch attribution). The ref is
 * cleared on a successful complete; a transient network failure preserves
 * it so a retry still carries attribution.
 *
 * For server-resolved (layer 3) flows, pendingRef is cleared without
 * being sent — the row already exists, partner_ref is first-touch and
 * locked in by the original sign-up, and a stale pendingRef in
 * localStorage would only confuse a future fresh sign-up on this device.
 *
 * On success, navigates to /me. On failure, a recoverable error UI —
 * the typical cause is a transient network blip and a reload resolves it.
 */
export default function OnboardingCompletePage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { isLoaded, isSignedIn, user: clerkUser } = useClerkUser();
  const [state, setState] = useState<LoadState>('reconciling');
  const [errorMessage, setErrorMessage] = useState<string>('');
  // Guards the one-shot auto-resolve so a dependency change (e.g. the
  // Clerk user object getting a new reference) cannot re-fire it.
  const hasRunRef = useRef(false);
  // Holds the in-flight resolve's controller so it can be aborted on
  // unmount — preventing a late-resolving request from firing a stray
  // navigate after the user has moved on.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);
  const reconcile = useCallback(
    async (townSlug: string, signal?: AbortSignal): Promise<void> => {
      setState('reconciling');
      // Read the partner ref captured by JoinPage (if any). Read once,
      // before the API call, so the value is stable for both the
      // request body and the post-success clear. Reading from the
      // exact same key the API write will land against also means a
      // browser-storage clear between read and call is handled cleanly
      // — we'd simply send no ref, attribution goes NULL.
      const pendingRef = readPendingRef();
      try {
        const user = await completeOnboarding({
          townSlug,
          getToken,
          ...(pendingRef !== null ? { ref: pendingRef.ref } : {}),
          ...(signal !== undefined ? { signal } : {}),
        });
        if (signal?.aborted) return;
        // API call succeeded — clear the captured ref. A later sign-up
        // by a different guest on the same device must not inherit
        // this attribution. Cleared AFTER the await so a transient
        // failure (network blip, 5xx) preserves the ref for the retry
        // path; the API endpoint is idempotent and partner_ref is
        // first-touch, so a retry with the same ref is harmless.
        clearPendingRef();
        // personalPage carries only the route-hint trio. The town comes
        // back from the API as authoritative; joinedAt is the server value.
        writePersonalPage({
          token: user.townSlug,
          town: user.townSlug,
          joinedAt: user.joinedAt,
        });
        navigate('/me', { replace: true });
      } catch (err) {
        // Aborted mid-flight (component unmounted) — drop silently.
        if (signal?.aborted) return;
        const message =
          err instanceof ApiError
            ? `Couldn't finish signing in (${err.code}). Please try again.`
            : "Couldn't reach the server. Check your connection and try again.";
        setErrorMessage(message);
        setState('error');
      }
    },
    [getToken, navigate],
  );
  /**
   * Layer 3 of town resolution — ask the server.
   *
   * Runs when both local non-interactive sources (personalPage and
   * Clerk publicMetadata) returned null. The typical caller is a
   * returning user whose localStorage was wiped on sign-out: their
   * user row exists server-side with the original town_slug +
   * partner_ref, but the client has lost all hints to it. Re-asking
   * them "Which town are you visiting?" when the server already knows
   * is unnecessary friction AND breaks partner brand continuity
   * (their town might be Padstow but their partner is Harlyn Sands —
   * the picker only shows the TOWN, losing the partner context).
   *
   * Three outcomes:
   *   - 200 with a launch-town slug → hydrate personalPage from the
   *     server response, clear any stale pendingRef, route to /me.
   *     No reconcile call: the row exists, attribution is first-touch
   *     and immutable.
   *   - 200 with a town slug NOT in LAUNCH_TOWN_SLUGS → treat as
   *     unresolved, show the picker. Matches the local-sources posture
   *     (asLaunchTownSlug returns null for unknown towns).
   *   - 404 'not_found' OR 403 'onboarding_required' → no user row
   *     exists yet. Both codes mean the same thing from this page's
   *     perspective even though they come from different layers
   *     (`not_found` is "/me passed auth but no users row"; the
   *     `onboarding_required` code is returned by the requireUser
   *     middleware which rejects the request before the handler runs
   *     when no users row exists). The page collapses both into the
   *     "show the picker" branch — without this collapse, a user who
   *     lands at /onboarding/complete without a town hint (no
   *     personalPage in localStorage, no publicMetadata.townSlug)
   *     would dump into the generic error state instead of being
   *     recovered. Fixed 10 Jun 2026 after the symptom surfaced
   *     during Harlyn-prep testing.
   *   - Any other error (network, 5xx, auth) → recoverable error UI.
   *     We deliberately do NOT fall through to the picker on these:
   *     letting the picker run on a transient network failure could
   *     create a duplicate user row if the original row exists but
   *     the /me call just couldn't reach the server. Failing closed
   *     is safer than failing open here.
   */
  const hydrateFromServer = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      setState('reconciling');
      try {
        const user = await getMe({
          getToken,
          ...(signal !== undefined ? { signal } : {}),
        });
        if (signal?.aborted) return;
        // Defensive: the API might know towns we don't have personas
        // for yet (during a new-town rollout the server seeds the
        // town first; the app catches up on the next deploy). Treat
        // unknown towns as unresolved — same posture as
        // asLaunchTownSlug applied to local sources above.
        if (asLaunchTownSlug(user.townSlug) === null) {
          setState('needs-town');
          return;
        }
        // Returning user — clear any stale pendingRef. partner_ref is
        // first-touch and locked in by the original sign-up, so a
        // ref from a later QR scan would be ignored server-side
        // anyway; we clear locally so the next fresh sign-up on this
        // device isn't misled by a stale value.
        clearPendingRef();
        writePersonalPage({
          token: user.townSlug,
          town: user.townSlug,
          joinedAt: user.joinedAt,
        });
        navigate('/me', { replace: true });
      } catch (err) {
        if (signal?.aborted) return;
        // Both codes mean "no user row yet" — see the function
        // doc-comment for the full reasoning. Falling through to the
        // picker is the correct UX for either:
        //   - 'not_found' (404): /me passed auth but no users row
        //     exists (the bare-URL first-touch case).
        //   - 'onboarding_required' (403): the requireUser middleware
        //     rejected the request because no users row exists for
        //     this Clerk session. Happens when a user lands at
        //     /onboarding/complete without first going through
        //     JoinPage (no personalPage in localStorage, no
        //     publicMetadata.townSlug) — e.g. via a direct /sign-in
        //     arrival, a Clerk-redirect flow we haven't anticipated,
        //     or a test browser with mixed state.
        // If the API ever changes either code string for these
        // conditions, this branch needs updating; it's the load-
        // bearing distinction between "first touch" and "transient
        // failure".
        if (
          err instanceof ApiError &&
          (err.code === 'not_found' || err.code === 'onboarding_required')
        ) {
          setState('needs-town');
          return;
        }
        const message =
          err instanceof ApiError
            ? `Couldn't finish signing in (${err.code}). Please try again.`
            : "Couldn't reach the server. Check your connection and try again.";
        setErrorMessage(message);
        setState('error');
      }
    },
    [getToken, navigate],
  );
  useEffect(() => {
    if (!isLoaded || hasRunRef.current) return;
    if (!isSignedIn) {
      // Outside the documented flow — defensive bounce.
      navigate('/sign-in', { replace: true });
      return;
    }
    hasRunRef.current = true;
    const localTown =
      asLaunchTownSlug(readPersonalPage()?.town) ??
      asLaunchTownSlug(clerkUser?.publicMetadata.townSlug);
    const controller = new AbortController();
    abortRef.current = controller;
    if (localTown !== null) {
      // Layer 1 or 2 hit — first-touch reconcile (or idempotent fetch
      // of an existing row if the user signed up earlier on this same
      // device).
      void reconcile(localTown, controller.signal);
    } else {
      // Local layers exhausted — ask the server. Resolves the picker-
      // for-returning-users bug, leaves the picker only for genuine
      // bare-URL first-touch arrivals (server 404).
      void hydrateFromServer(controller.signal);
    }
  }, [
    isLoaded,
    isSignedIn,
    clerkUser,
    navigate,
    reconcile,
    hydrateFromServer,
  ]);
  const launchTowns = LAUNCH_TOWN_SLUGS.map((slug) => getPersona(slug)).filter(
    (persona): persona is Persona => persona !== null,
  );
  return (
    <>
      <Helmet>
        <title>Finishing up · TownInsider</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <main
        id="main-content"
        className="container-narrow safe-top safe-bottom flex min-h-dvh flex-col items-center justify-center gap-4 px-4 py-8 text-center"
      >
        {state === 'reconciling' && (
          <>
            <Loader2
              className="h-6 w-6 animate-spin text-moor-500"
              aria-hidden="true"
            />
            <p className="text-sm text-moor-600">Just a moment…</p>
          </>
        )}
        {state === 'needs-town' && (
          <div className="flex w-full max-w-sm flex-col gap-4">
            <h1 className="font-display text-2xl text-moor-800">
              Which town are you visiting?
            </h1>
            <p className="text-sm text-slate-700">
              We couldn't tell which town you're visiting — choose it below
              and we'll finish setting you up.
            </p>
            <div className="mt-2 flex flex-col gap-3">
              {launchTowns.map((persona) => (
                <button
                  key={persona.slug}
                  type="button"
                  onClick={() => void reconcile(persona.slug)}
                  className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-left transition-colors hover:border-moor-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
                >
                  <span>
                    <span className="block font-display text-lg text-moor-800">
                      {persona.townLabel}
                    </span>
                    <span className="block text-sm text-slate-600">
                      Your Insider: {persona.personaName}
                    </span>
                  </span>
                  <ChevronRight
                    className="h-5 w-5 shrink-0 text-moor-500"
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
        {state === 'error' && (
          <>
            <h1 className="font-display text-2xl text-moor-800">
              Something's gone wrong
            </h1>
            <p className="max-w-sm text-sm text-slate-700">{errorMessage}</p>
            <button
              type="button"
              onClick={() => {
                // Hard reload — re-runs reconciliation from a clean slate
                // and re-fetches any stale Clerk state.
                window.location.reload();
              }}
              className="mt-2 rounded-full bg-moor-700 px-5 py-2 text-sm font-medium text-cream-50 transition-colors hover:bg-moor-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              Try again
            </button>
          </>
        )}
      </main>
    </>
  );
}
